/**
 * GA4 sink — loads gtag.js and forwards page views / events.
 *
 * The canonical snippet defines `window.dataLayer` and the `gtag` shim
 * synchronously, so events fired before the async script loads are queued
 * rather than lost. If the script is blocked (ad blocker), calls simply queue
 * into an array that never flushes — harmless, and never throws.
 *
 * SPA note: config is sent with `send_page_view: false`; we send page views
 * manually on route change so the first load isn't double-counted.
 */

import { GA_MEASUREMENT_ID, gaEnabled } from './config';

let initialized = false;

const gtag = (...args) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  // gtag pushes `arguments` (not an array) onto dataLayer.
  window.dataLayer.push(args);
};

/** Inject gtag.js once. Safe to call repeatedly. */
export const initGa = () => {
  if (initialized || !gaEnabled() || typeof document === 'undefined') {
    return;
  }
  initialized = true;

  try {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
      GA_MEASUREMENT_ID,
    )}`;
    document.head.appendChild(script);

    gtag('js', new Date());
    // We control page views manually (SPA).
    gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
  } catch (e) {
    // Never let analytics setup break the app.
  }
};

/**
 * @param {string} path - redacted path+search
 * @param {string} [title]
 */
export const gaPageView = (path, title) => {
  if (!gaEnabled()) {
    return;
  }
  try {
    gtag('event', 'page_view', {
      page_path: path,
      page_title: title || (typeof document !== 'undefined' ? document.title : undefined),
    });
  } catch (e) {
    // swallow
  }
};

/**
 * @param {string} name
 * @param {Object} [params]
 */
export const gaEvent = (name, params = {}) => {
  if (!gaEnabled()) {
    return;
  }
  try {
    gtag('event', name, params);
  } catch (e) {
    // swallow
  }
};
